// 네이티브 푸시 알림 탭 처리 — 알림을 눌렀을 때 지정된 화면으로 이동시킨다.
//
// 백엔드 FCMNotificationService.send_to_user()가 payload의 data.url에 이동할 경로를
// 실어 보내는데, 앱에 pushNotificationActionPerformed 리스너가 없으면 알림을 탭해도
// 앱만 열리고 아무 화면으로도 가지 않는다(에뮬레이터 검증에서 확인).
//
// 리스너는 앱 전체에서 한 번만 등록해야 한다. usePushNotification 훅은 App/StudentHome/
// ParentShell 세 곳에서 쓰여 동시에 마운트될 수 있어, 훅 안에 두면 탭 한 번에 이동이
// 여러 번 일어난다. 그래서 모듈 수준 싱글턴으로 분리했다.
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';

let initialized = false;

/** 포그라운드로 알림이 도착했을 때 앱 내부에 알리는 이벤트 이름 */
export const PUSH_RECEIVED_EVENT = 'jigi:push-received';

export interface PushReceivedDetail {
  title: string;
  body: string;
}

/** 알림 페이로드의 url로 이동 — 외부 URL 주입을 막기 위해 앱 내부 경로만 허용한다. */
function navigateTo(url: unknown): void {
  if (typeof url !== 'string') return;
  if (!url.startsWith('/') || url.startsWith('//')) return;

  window.history.pushState({}, '', url);
  // react-router가 popstate를 듣고 있어 이 이벤트로 SPA 라우팅이 이루어진다.
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export function initPushNotificationHandlers(): void {
  if (!Capacitor.isNativePlatform() || initialized) return;
  initialized = true;

  PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    navigateTo(action.notification?.data?.url);
  }).catch((err) => {
    console.warn('[Push] 알림 탭 리스너 등록 실패', err);
  });

  // 앱을 보고 있는 중(포그라운드)에 도착한 알림은 Android가 상태바에 띄우지 않고
  // 앱으로 바로 전달한다. 표시할 곳이 없으면 그대로 사라지므로(에뮬레이터 검증에서 확인)
  // 이벤트로 넘겨 앱 안에서 토스트로 보여준다.
  // 여기서 직접 토스트를 띄우지 않는 이유: 이 모듈은 React 밖에서 한 번만 초기화되어
  // useToast를 쓸 수 없다. 대신 ToastProvider 안의 PushToastBridge가 받아서 띄운다.
  PushNotifications.addListener('pushNotificationReceived', (notification) => {
    const detail: PushReceivedDetail = {
      title: notification.title ?? '',
      body: notification.body ?? '',
    };
    window.dispatchEvent(new CustomEvent<PushReceivedDetail>(PUSH_RECEIVED_EVENT, { detail }));
  }).catch((err) => {
    console.warn('[Push] 알림 수신 리스너 등록 실패', err);
  });
}
